import { useEffect, useRef } from 'react';
import { materialConfig } from '../configurator/materials/materialConfig';
import { loadMaterialTextures as loadKTX2MaterialTextures } from '../utils/KTX2TextureLoader';
import { useThree } from '@react-three/fiber';
import { getCachedMaterial, setCachedMaterial, isMaterialCached } from '../utils/materialCache';

/**
 * Hook to preload all material textures immediately when the Canvas loads
 * This runs independently from WebSocket connection
 * Uses shared materialCache to prevent duplicate loading
 */
export const useTexturePreloader = (onLoadingComplete) => {
  const { gl } = useThree();
  const loadingStateRef = useRef({
    totalTextures: 0,
    loadedTextures: 0,
    isComplete: false
  });

  useEffect(() => {
    if (!gl) return;

    console.log('🚀 [TexturePreloader] Starting texture preload...');
    const materialKeys = Object.keys(materialConfig);

    // Calculate total number of textures to load
    let totalTextures = 0;
    materialKeys.forEach((key) => {
      const matConfig = materialConfig[key];
      if (matConfig && matConfig.textures) {
        totalTextures += Object.keys(matConfig.textures).filter(texKey => matConfig.textures[texKey]).length;
      }
    });

    loadingStateRef.current.totalTextures = totalTextures;
    loadingStateRef.current.loadedTextures = 0;
    loadingStateRef.current.isComplete = false;

    console.log(`📊 [TexturePreloader] Total textures to load: ${totalTextures}`);

    // Load all materials in parallel
    const loadAllMaterials = async () => {
      const startTime = performance.now();

      await Promise.all(materialKeys.map(async (materialKey) => {
        // Check shared cache first
        if (isMaterialCached(materialKey)) {
          console.log(`♻️ [TexturePreloader] Material already cached: ${materialConfig[materialKey]?.name}`);
          return getCachedMaterial(materialKey);
        }

        const matConfig = materialConfig[materialKey];
        if (!matConfig) {
          console.error(`[TexturePreloader] Material ${materialKey} not found in materialConfig`);
          return null;
        }

        console.log(`📦 [TexturePreloader] Loading textures for ${matConfig.name}...`);

        try {
          // Load textures using KTX2 with fallback to PNG/JPG
          const textures = await loadKTX2MaterialTextures(matConfig, gl, true);

          // Configure texture scaling
          const totalTextures = Object.keys(textures).length;
          Object.values(textures).forEach((texture) => {
            if (texture) {
              texture.repeat.set(matConfig.textureScale, matConfig.textureScale);
            }
          });

          // Update loading state
          loadingStateRef.current.loadedTextures += totalTextures;
          const progress = Math.round((loadingStateRef.current.loadedTextures / loadingStateRef.current.totalTextures) * 100);
          console.log(`  ✅ [TexturePreloader] ${matConfig.name} loaded (${totalTextures} textures) - Progress: ${progress}%`);

          const materialData = { textures, properties: matConfig.properties };

          // Store in shared cache
          setCachedMaterial(materialKey, materialData);

          return materialData;
        } catch (error) {
          console.error(`❌ [TexturePreloader] Error loading ${matConfig.name}:`, error);
          loadingStateRef.current.loadedTextures += 1; // Still increment to avoid stuck loading
          return null;
        }
      }));

      const endTime = performance.now();
      const duration = ((endTime - startTime) / 1000).toFixed(2);

      loadingStateRef.current.isComplete = true;
      console.log(`✅ [TexturePreloader] All textures loaded in ${duration}s!`);

      if (onLoadingComplete) {
        onLoadingComplete();
      }
    };

    loadAllMaterials();
  }, [gl, onLoadingComplete]);

  return {
    loadingState: loadingStateRef.current
  };
};
